import { z } from 'zod';
import { actorSnapshotSchema, entityIdSchema, nonEmptyTextSchema, sha256Schema, utcDateTimeSchema, } from '@kop/contracts';
export const workKinds = ['non-routine', 'holiday', 'emergency', 'policy-high-risk'];
export const workKindSchema = z.enum(workKinds);
export const caseStatuses = [
    'draft',
    'submitted',
    'revision-requested',
    'pre-approved',
    'induction-complete',
    'tbm-complete',
    'active',
    'stopped',
    'completion-requested',
    'closed',
    'cancelled',
    'expired',
];
export const caseStatusSchema = z.enum(caseStatuses);
export const contractorMasterSchema = z
    .object({
    contractorId: entityIdSchema,
    legalName: nonEmptyTextSchema.max(200),
    businessRegistrationNumber: nonEmptyTextSchema.max(40),
    kind: z.enum(['resident', 'temporary']),
    lifecycle: z.enum(['active', 'inactive', 'archived']),
    createdAt: utcDateTimeSchema,
})
    .strict();
export const siteEngagementSchema = z
    .object({
    engagementId: entityIdSchema,
    contractorId: entityIdSchema,
    tenantId: entityIdSchema,
    siteId: entityIdSchema,
    contractReference: z.string().trim().max(100).nullable(),
    workScope: nonEmptyTextSchema.max(1_000),
    startsOn: z.iso.date(),
    endsOn: z.iso.date(),
    status: z.enum(['pending', 'active', 'ended', 'suspended']),
})
    .strict()
    .superRefine((value, context) => {
    if (value.endsOn < value.startsOn) {
        context.addIssue({
            code: 'custom',
            path: ['endsOn'],
            message: 'Engagement end must not precede start',
        });
    }
});
export const externalClaimSchema = z
    .object({
    claimId: entityIdSchema,
    caseId: entityIdSchema,
    email: z.email(),
    contractorId: entityIdSchema,
    scope: z.literal('own-work-control-case'),
    issuedAt: utcDateTimeSchema,
    expiresAt: utcDateTimeSchema,
    consumedAt: utcDateTimeSchema.nullable(),
    revokedAt: utcDateTimeSchema.nullable(),
    promotedMembershipId: entityIdSchema.nullable(),
})
    .strict();
export const signatureSchema = z
    .object({
    signerIdentityId: entityIdSchema.nullable(),
    signerName: nonEmptyTextSchema.max(100),
    signedAt: utcDateTimeSchema,
    meaning: z.enum([
        'prepared',
        'reviewed',
        'approved',
        'attended-tbm',
        'inspected',
        'completion-confirmed',
    ]),
    imageEvidenceId: entityIdSchema.nullable(),
})
    .strict();
export const evidenceSnapshotSchema = z
    .object({
    evidenceId: entityIdSchema,
    category: z.enum([
        'application',
        'induction',
        'tbm',
        'control',
        'patrol',
        'completion',
        'signature',
    ]),
    mediaManifestId: entityIdSchema,
    contentHash: sha256Schema,
    capturedAt: utcDateTimeSchema,
    serverCommitted: z.boolean(),
    immutable: z.boolean(),
    caption: nonEmptyTextSchema.max(500),
})
    .strict();
export const workerSchema = z
    .object({
    workerId: entityIdSchema,
    name: nonEmptyTextSchema.max(100),
    role: nonEmptyTextSchema.max(100),
    mobileLast4: z
        .string()
        .regex(/^\d{4}$/)
        .nullable(),
    joinedAt: utcDateTimeSchema,
    leftAt: utcDateTimeSchema.nullable(),
    tbmSignature: signatureSchema.nullable(),
    briefingRevision: z.number().int().nonnegative(),
})
    .strict();
export const workStepSchema = z
    .object({
    stepId: entityIdSchema,
    order: z.number().int().positive(),
    description: nonEmptyTextSchema.max(500),
    hazards: z.array(nonEmptyTextSchema.max(300)).min(1),
    controls: z.array(nonEmptyTextSchema.max(500)).min(1),
})
    .strict();
export const patrolSchema = z
    .object({
    patrolId: entityIdSchema,
    inspectedAt: utcDateTimeSchema,
    inspector: actorSnapshotSchema,
    result: z.enum(['conforming', 'corrected-on-site', 'work-stopped']),
    note: nonEmptyTextSchema.max(1_000),
    evidenceIds: z.array(entityIdSchema).min(1),
    offlineCaptured: z.boolean(),
    serverReceivedAt: utcDateTimeSchema.nullable(),
})
    .strict();
export const stateEventSchema = z
    .object({
    eventId: entityIdSchema,
    action: nonEmptyTextSchema.max(100),
    from: caseStatusSchema,
    to: caseStatusSchema,
    actorId: entityIdSchema,
    occurredAt: utcDateTimeSchema,
    reason: z.string().trim().max(2_000).nullable(),
    idempotencyKey: entityIdSchema,
})
    .strict();
export const workControlCaseSchema = z
    .object({
    caseId: entityIdSchema,
    version: z.number().int().positive(),
    tenantId: entityIdSchema,
    siteId: entityIdSchema,
    contractorSnapshot: contractorMasterSchema,
    engagementSnapshot: siteEngagementSchema.nullable(),
    accessMode: z.enum(['workspace-membership', 'email-invite', 'one-time-claim']),
    kind: workKindSchema,
    triggerReasons: z.array(nonEmptyTextSchema.max(300)).min(1),
    title: nonEmptyTextSchema.max(300),
    area: nonEmptyTextSchema.max(300),
    startsAt: utcDateTimeSchema,
    expiresAt: utcDateTimeSchema,
    emergencyOrHolidayReason: z.string().trim().max(1_000).nullable(),
    retrospectiveApprovalRequired: z.boolean(),
    retrospectiveApprovedAt: utcDateTimeSchema.nullable(),
    equipment: z.array(nonEmptyTextSchema.max(200)),
    steps: z.array(workStepSchema).min(1),
    workers: z.array(workerSchema).min(1),
    induction: z
        .object({
        completedAt: utcDateTimeSchema,
        instructor: actorSnapshotSchema,
        evidenceIds: z.array(entityIdSchema).min(1),
    })
        .strict()
        .nullable(),
    tbm: z
        .object({
        completedAt: utcDateTimeSchema,
        facilitator: actorSnapshotSchema,
        planRevision: z.number().int().positive(),
        evidenceIds: z.array(entityIdSchema).min(1),
    })
        .strict()
        .nullable(),
    preApproval: z
        .object({
        approvedAt: utcDateTimeSchema,
        approver: actorSnapshotSchema,
        opinion: nonEmptyTextSchema.max(2_000),
    })
        .strict()
        .nullable(),
    permit: z
        .object({
        activatedAt: utcDateTimeSchema,
        validUntil: utcDateTimeSchema,
        activatedBy: actorSnapshotSchema,
    })
        .strict()
        .nullable(),
    patrols: z.array(patrolSchema),
    stopHistory: z.array(z
        .object({
        stoppedAt: utcDateTimeSchema,
        stoppedBy: actorSnapshotSchema,
        reason: nonEmptyTextSchema.max(1_000),
        resumedAt: utcDateTimeSchema.nullable(),
        resumedBy: actorSnapshotSchema.nullable(),
    })
        .strict()),
    extensions: z.array(z
        .object({
        previousExpiry: utcDateTimeSchema,
        extendedUntil: utcDateTimeSchema,
        reason: nonEmptyTextSchema.max(1_000),
        approvedBy: actorSnapshotSchema,
        approvedAt: utcDateTimeSchema,
    })
        .strict()),
    completion: z
        .object({
        requestedAt: utcDateTimeSchema,
        allWorkersExited: z.boolean(),
        damageChecked: z.boolean(),
        housekeepingChecked: z.boolean(),
        evidenceIds: z.array(entityIdSchema).min(1),
        contractorSignature: signatureSchema,
    })
        .strict()
        .nullable(),
    evidence: z.array(evidenceSnapshotSchema),
    status: caseStatusSchema,
    stateEvents: z.array(stateEventSchema),
    contentRevision: z.number().int().positive(),
    integrityLock: z
        .object({ contentHash: sha256Schema, lockedAt: utcDateTimeSchema, lockedBy: entityIdSchema })
        .strict()
        .nullable(),
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (Date.parse(value.expiresAt) <= Date.parse(value.startsAt)) {
        context.addIssue({
            code: 'custom',
            path: ['expiresAt'],
            message: 'Permit expiry must follow work start',
        });
    }
    if ((value.kind === 'holiday' || value.kind === 'emergency') &&
        !value.emergencyOrHolidayReason) {
        context.addIssue({
            code: 'custom',
            path: ['emergencyOrHolidayReason'],
            message: 'Holiday and emergency work require a reason',
        });
    }
    if (value.status === 'closed' && !value.integrityLock) {
        context.addIssue({
            code: 'custom',
            path: ['integrityLock'],
            message: 'Closed evidence packages must be integrity locked',
        });
    }
    if (value.status !== 'closed' && value.integrityLock) {
        context.addIssue({
            code: 'custom',
            path: ['integrityLock'],
            message: 'Only closed cases may be locked',
        });
    }
});
